import React, { useState } from 'react';
import { Box, ButtonBase, Paper, Stack, Typography } from '@mui/material';
import {
  Flame,
  Moon,
  Sparkles,
  Sun,
  Target,
  Trees,
} from 'lucide-react';

export type GameTheme = 'archer' | 'tank';

interface Props {
  onSelect: (theme: GameTheme) => void;
}

export const GameSelector: React.FC<Props> = ({ onSelect }) => {
  const [hovered, setHovered] = useState<GameTheme | null>(null);

  return (
    <Box
      className='cca-gs-root'
      sx={{
        minHeight: '100vh',
        bgcolor: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        className='cca-gs-stars'
        aria-hidden='true'
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.25,
          pointerEvents: 'none',
        }}
      />

      <Box
        className='cca-gs-content'
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 900,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 1.5,
              borderRadius: '50%',
              bgcolor: 'rgba(16, 185, 129, 0.15)',
              color: '#10b981',
              mb: 1.5,
            }}
          >
            <Target size={32} />
          </Box>
          <Typography
            variant='h4'
            fontWeight={800}
            color='#ffffff'
            sx={{ mb: 1 }}
          >
            Choose Your Challenge
          </Typography>
          <Typography variant='body1' color='rgba(255, 255, 255, 0.7)'>
            Same assessment — different game experience
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          justifyContent='center'
          sx={{ mb: 4 }}
        >
          {/* Archer card */}
          <ButtonBase
            sx={{
              flex: 1,
              borderRadius: 4,
              overflow: 'hidden',
              textAlign: 'left',
              display: 'block',
              transition: 'transform 0.2s, box-shadow 0.2s',
              border: '2px solid',
              borderColor:
                hovered === 'archer' ? '#10b981' : 'rgba(255, 255, 255, 0.1)',
              boxShadow:
                hovered === 'archer'
                  ? '0 20px 25px -5px rgba(16, 185, 129, 0.2)'
                  : 'none',
              transform: hovered === 'archer' ? 'translateY(-4px)' : 'none',
            }}
            onMouseEnter={() => setHovered('archer')}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onSelect('archer')}
          >
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: 'rgba(30, 41, 59, 0.8)',
                backdropFilter: 'blur(8px)',
                color: '#ffffff',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <svg viewBox='0 0 80 120' width='80' height='120'>
                  <ellipse cx='40' cy='118' rx='18' ry='4' fill='rgba(0,0,0,0.3)' />
                  <rect x='30' y='90' width='10' height='22' rx='4' fill='#1b4332' />
                  <rect x='40' y='90' width='10' height='22' rx='4' fill='#1b4332' />
                  <path d='M22 55 Q20 96 32 110 L48 110 Q58 96 56 55 Z' fill='#2d6a4f' />
                  <rect x='20' y='88' width='38' height='7' rx='2' fill='#5c3317' />
                  <rect x='36' y='87' width='8' height='9' rx='2' fill='#c8a84b' />
                  <circle cx='40' cy='38' r='16' fill='#f4d4a0' />
                  <path d='M29 35 Q31 18 51 18 Q57 35 29 35Z' fill='#1b4332' />
                  <path d='M51 28 C57 18 64 22 60 32 C57 24 54 25 51 28Z' fill='#c41e3a' />
                  <line x1='24' y1='64' x2='4' y2='60' stroke='#f4d4a0' strokeWidth='7' strokeLinecap='round' />
                  <path d='M4 46 Q-8 62 4 78' stroke='#8B5A2B' strokeWidth='4' fill='none' strokeLinecap='round' />
                  <line x1='56' y1='64' x2='72' y2='60' stroke='#f4d4a0' strokeWidth='7' strokeLinecap='round' />
                  <line x1='4' y1='46' x2='36' y2='62' stroke='#d4c088' strokeWidth='1.5' />
                  <line x1='36' y1='62' x2='4' y2='78' stroke='#d4c088' strokeWidth='1.5' />
                  <line x1='2' y1='62' x2='36' y2='62' stroke='#c8a84b' strokeWidth='2.5' strokeLinecap='round' />
                </svg>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: 'rgba(16, 185, 129, 0.2)',
                    color: '#34d399',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  <Trees size={14} /> Forest
                </Box>
                <Typography variant='h6' fontWeight={700} color='#ffffff'>
                  Robinhood Archer
                </Typography>
                <Typography
                  variant='body2'
                  color='rgba(255, 255, 255, 0.65)'
                  sx={{ mt: 0.5 }}
                >
                  Medieval forest setting. Draw your bow and fire arrows at the
                  correct answer.
                </Typography>
              </Box>

              <Stack spacing={1} sx={{ my: 2, flex: 1 }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Target size={16} color='#34d399' />
                  <Typography variant='caption' color='rgba(255, 255, 255, 0.8)'>
                    SVG archer with bow animation
                  </Typography>
                </Stack>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Sparkles size={16} color='#34d399' />
                  <Typography variant='caption' color='rgba(255, 255, 255, 0.8)'>
                    Arrow flies in a graceful arc
                  </Typography>
                </Stack>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Moon size={16} color='#34d399' />
                  <Typography variant='caption' color='rgba(255, 255, 255, 0.8)'>
                    Dusk forest atmosphere
                  </Typography>
                </Stack>
              </Stack>

              <Typography
                variant='button'
                sx={{
                  color: '#34d399',
                  fontWeight: 700,
                  display: 'block',
                  textAlign: 'right',
                  mt: 'auto',
                }}
              >
                Select Archer →
              </Typography>
            </Paper>
          </ButtonBase>

          {/* Tank card */}
          <ButtonBase
            sx={{
              flex: 1,
              borderRadius: 4,
              overflow: 'hidden',
              textAlign: 'left',
              display: 'block',
              transition: 'transform 0.2s, box-shadow 0.2s',
              border: '2px solid',
              borderColor:
                hovered === 'tank' ? '#f59e0b' : 'rgba(255, 255, 255, 0.1)',
              boxShadow:
                hovered === 'tank'
                  ? '0 20px 25px -5px rgba(245, 158, 11, 0.2)'
                  : 'none',
              transform: hovered === 'tank' ? 'translateY(-4px)' : 'none',
            }}
            onMouseEnter={() => setHovered('tank')}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onSelect('tank')}
          >
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: 'rgba(30, 41, 59, 0.8)',
                backdropFilter: 'blur(8px)',
                color: '#ffffff',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <svg viewBox='0 0 130 80' width='130' height='80'>
                  <ellipse cx='65' cy='78' rx='50' ry='4' fill='rgba(0,0,0,0.3)' />
                  <rect x='10' y='52' width='110' height='22' rx='11' fill='#2a2a1e' />
                  {[26, 44, 62, 80, 98].map((cx, i) => (
                    <circle
                      key={i}
                      cx={cx}
                      cy='63'
                      r='9'
                      fill='#3d3d2a'
                      stroke='#1a1a10'
                      strokeWidth='1.5'
                    />
                  ))}
                  <path
                    d='M18 30 L112 30 L120 44 L122 52 L8 52 L10 44 Z'
                    fill='#5a6438'
                  />
                  <path
                    d='M30 26 Q32 12 65 10 Q98 12 100 26 L100 30 L30 30 Z'
                    fill='#4e5e2a'
                  />
                  <line
                    x1='65'
                    y1='20'
                    x2='126'
                    y2='16'
                    stroke='#2e3818'
                    strokeWidth='8'
                    strokeLinecap='round'
                  />
                  <line
                    x1='65'
                    y1='19'
                    x2='126'
                    y2='15'
                    stroke='#3e4a24'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <circle cx='126' cy='16' r='4.5' fill='#252e12' />
                  <ellipse cx='65' cy='12' rx='9' ry='3.5' fill='#3a4520' />
                </svg>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: 'rgba(245, 158, 11, 0.2)',
                    color: '#fbbf24',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  <Sun size={14} /> Desert
                </Box>
                <Typography variant='h6' fontWeight={700} color='#ffffff'>
                  Tank Commander
                </Typography>
                <Typography
                  variant='body2'
                  color='rgba(255, 255, 255, 0.65)'
                  sx={{ mt: 0.5 }}
                >
                  Desert warzone setting. Aim your cannon and fire shells at the
                  correct answer.
                </Typography>
              </Box>

              <Stack spacing={1} sx={{ my: 2, flex: 1 }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Target size={16} color='#fbbf24' />
                  <Typography variant='caption' color='rgba(255, 255, 255, 0.8)'>
                    Detailed 2D realistic tank SVG
                  </Typography>
                </Stack>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Flame size={16} color='#fbbf24' />
                  <Typography variant='caption' color='rgba(255, 255, 255, 0.8)'>
                    Shell blast with muzzle flash
                  </Typography>
                </Stack>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Sun size={16} color='#fbbf24' />
                  <Typography variant='caption' color='rgba(255, 255, 255, 0.8)'>
                    Dramatic sunset atmosphere
                  </Typography>
                </Stack>
              </Stack>

              <Typography
                variant='button'
                sx={{
                  color: '#fbbf24',
                  fontWeight: 700,
                  display: 'block',
                  textAlign: 'right',
                  mt: 'auto',
                }}
              >
                Select Tank →
              </Typography>
            </Paper>
          </ButtonBase>
        </Stack>

        <Typography variant='caption' color='rgba(255, 255, 255, 0.5)'>
          Both modes cover the same assessment — pick your style
        </Typography>
      </Box>
    </Box>
  );
};

export default GameSelector;
